import { Component, EventEmitter, Inject, Injectable, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { translate } from '@ngneat/transloco';
import { BehaviorSubject, Subscription } from 'rxjs';
import { UtilsService } from '@taxilla-library/lib/services/utils/utils.service';

// Define an interface for the FileUpload service to avoid direct import
@Injectable()
export abstract class IFileUpload {
    abstract upload(
        files: File[],
        data: any,
        url: string,
        progressCallBack: (progress: number) => void,
        successCallBack: (response: any) => void,
        failureCallBack: (error: any) => void
    ): void;
}

@Component({
    selector: 'app-file-upload',
    templateUrl: './upload.component.html',
    styleUrls: ['./upload.component.scss'],
    standalone: false,
})
export class UploadComponent implements OnInit, OnDestroy, OnChanges {
    @Input() id = 'fileUpload';
    @Input() accept = '*/*';
    @Input() multiple = false;
    @Input() maxFileSize = 10; // MB
    @Input() allowedFileTypes: string[] = [];
    @Input() clearFiles?: BehaviorSubject<boolean>;
    @Input() uploadUrl = '';
    @Input() uploadData: any = {};
    @Input() data: any;
    @Input() supportedFormats: string[] = [];
    @Input() isDisabled = false;
    @Input() color?: string;
    @Input() appendedFileName?: string;

    @Output() newValue = new EventEmitter<FileList | any>();
    @Output() fileName = new EventEmitter<string>();
    @Output() isFileTypeSupported = new EventEmitter<boolean>();

    isFileSelected = false;
    private clearFilesSubscription?: Subscription;
    files: any;
    fileHovered = false;
    dragDropText = '';
    acceptFormats = '';

    constructor(private _utils: UtilsService, @Inject('FileUpload') private fileUpload: IFileUpload) {
        this.id = this._utils.guid();
    }

    public onInputChange(event: { target: any }): void {
        // Handle null/undefined event or target
        if (!event || !event.target) {
            console.error('Invalid file input event');
            return;
        }

        const input: HTMLInputElement = event.target;

        // Handle case where files property is null/undefined
        if (!input.files || input.files.length === 0) {
            this.isFileSelected = false;
            return;
        }

        this.isFileSelected = input.files.length > 0;
        const files = input.files;
        const newFiles = this._utils.changeFilesContentType(files);
        if (!newFiles || newFiles.length === 0) {
            return;
        }
        let isSupported = true;
        this.newValue.emit(newFiles);
        for (let i = 0; i < newFiles.length; i++) {
            if (i === 0) {
                this.fileName.emit(newFiles[i].name);
            }
        }
        if (this.supportedFormats?.length) {
            for (let i = 0; i < newFiles.length; i++) {
                const fileExt = newFiles[i].name.substring(newFiles[i].name.lastIndexOf('.'), newFiles[i].name.length).toLowerCase();
                isSupported = isSupported && (this.supportedFormats.indexOf(fileExt) >= 0 || ['.zip'].indexOf(fileExt) > -1);
            }
        }
        this.isFileTypeSupported.emit(isSupported);
        const slug = this.data.url;
        const data = {
            filename: this.data.filename,
        };

        if (!this.data.return) {
            this.fileUpload.upload(
                newFiles,
                data,
                slug,
                (progress: unknown) => console.log(progress),
                () => {
                    this._utils.alertSuccess(this.translateMsg('File has bean uploaded'));
                    setTimeout(() => this._utils.hideLoading(), 3000);
                },
                res => {
                    this._utils.alertError(res?.msg || this.translateMsg('Failed to upload file'));
                    this._utils.hideLoading();
                }
            );
        }
    }

    onFilesDropEvent(files: any): void {
        if (!files || !files.length) {
            console.error('No files provided for drop event');
            return;
        }

        // Convert FileList to array if needed
        const fileArray = Array.isArray(files) ? files : Array.from(files);

        // Create a new DataTransfer object to hold the files
        const dataTransfer = new DataTransfer();
        fileArray.forEach((file: File) => dataTransfer.items.add(file));

        this.onInputChange({
            target: {
                files: dataTransfer.files,
            },
        });
    }

    translateMsg = (msg: string) => translate(msg);

    openFileSelection(): void {
        (document.querySelector(`[id='${this.id}']`) as HTMLElement)?.click();
    }

    ngOnInit(): void {
        this.clearFilesSubscription = this.clearFiles?.subscribe(() => {
            this.isFileSelected = false;
        });
        this.updateComputedProperties();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.supportedFormats || changes.data) {
            this.updateComputedProperties();
        }
    }

    private updateComputedProperties(): void {
        this.dragDropText = this.translateMsg('Drag and drop file here');
        this.acceptFormats = this.supportedFormats?.join(',') || '';
    }

    ngOnDestroy(): void {
        if (this.clearFilesSubscription) {
            this.clearFilesSubscription.unsubscribe();
        }
    }
}
